import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, User, Shield, Mail, Phone, Building2, Calendar,
  Clock, Key, Edit, Save, RefreshCw, Send, Ban, CheckCircle,
  Trash2, Activity, Globe, Monitor, ShieldOff, AlertTriangle,
  MoreVertical, UserX, FileText, Copy, Download, Paperclip, X
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

// Built-in roles
const BUILT_IN_ROLES = {
  super_admin: { name: "Super Admin", description: "Full access to all features" },
  admin: { name: "Admin", description: "Administrative access without system settings" },
  support: { name: "Customer Support", description: "Handle customer tickets and basic support" },
  finance: { name: "Finance", description: "Access to billing and financial data" },
  marketing: { name: "Marketing", description: "Access to CMS and email campaigns" },
  viewer: { name: "Viewer", description: "Read-only access to dashboard" }
};

// Professional email templates
const EMAIL_TEMPLATES = [
  { 
    id: "general", 
    name: "General Query",
    subject: "Regarding Your Account",
    message: "Dear {name},\n\nI hope this message finds you well.\n\n[Your message here]\n\nPlease don't hesitate to reach out if you have any questions.\n\nBest regards"
  },
  {
    id: "offer",
    name: "Special Offer",
    subject: "Exclusive Offer for You",
    message: "Dear {name},\n\nWe're pleased to share an exclusive offer with you!\n\n[Offer details here]\n\nThis offer is valid until [date]. Don't miss out!\n\nBest regards"
  },
  {
    id: "account",
    name: "Account Related",
    subject: "Important Information About Your Account",
    message: "Dear {name},\n\nWe're writing to inform you about an important update regarding your account.\n\n[Account details here]\n\nIf you have any questions, please don't hesitate to contact us.\n\nBest regards"
  },
  {
    id: "invoice",
    name: "Invoice Related",
    subject: "Regarding Your Invoice",
    message: "Dear {name},\n\nWe're writing regarding your recent invoice.\n\n[Invoice details here]\n\nIf you have any questions about this invoice, please contact our billing team.\n\nBest regards"
  },
  {
    id: "login",
    name: "Login Related",
    subject: "Account Access Information",
    message: "Dear {name},\n\nWe noticed some activity related to your account access.\n\n[Login details here]\n\nIf this wasn't you, please secure your account immediately by resetting your password.\n\nBest regards"
  },
  {
    id: "custom",
    name: "Custom Message",
    subject: "",
    message: ""
  }
];

export default function InternalUserDetailsPage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { theme, user: currentUser } = useOutletContext();
  const isDark = theme === 'dark';

  // User data
  const [user, setUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Activity log
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    role: "",
    department: "",
    phone: ""
  });
  const [saving, setSaving] = useState(false);

  // Password reset
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Send email dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({
    subject: "",
    message: "",
    template: "general"
  });
  const [attachments, setAttachments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Terminate sessions
  const [terminatingSessions, setTerminatingSessions] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteType, setDeleteType] = useState("soft");
  const [deleting, setDeleting] = useState(false);

  // Revoke access dialog
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [revoking, setRevoking] = useState(false);

  // Block/Unblock dialog with reason
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockAction, setBlockAction] = useState("block"); // "block" or "unblock"
  const [blockReason, setBlockReason] = useState("");
  const [blocking, setBlocking] = useState(false);

  // Super admin protection
  const [superAdminCount, setSuperAdminCount] = useState(0);

  useEffect(() => {
    fetchUserData();
    fetchActivityLogs();
  }, [userId]);
const fetchUserData = async () => {
    setLoading(true);
    setLoadingStatus(true);
    try {
      // Fetch user status (includes all details)
      const statusRes = await axios.get(`${API}/internal/users/${userId}/status`, { withCredentials: true });
      setUserStatus(statusRes.data);
      setUser(statusRes.data);
      
      // Initialize edit form
      setEditForm({
        name: statusRes.data.name || "",
        email: statusRes.data.email || "",
        role: statusRes.data.role || "support",
        department: statusRes.data.department || "",
        phone: statusRes.data.phone || ""
      });

      // Fetch all internal users to count super admins
      const usersRes = await axios.get(`${API}/internal/users`, { withCredentials: true });
      const superAdmins = (usersRes.data.users || []).filter(u => u.role === 'super_admin' && u.is_active);
      setSuperAdminCount(superAdmins.length);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      toast.error("Failed to load user details");
      navigate('/backoffice/users');
    } finally {
      setLoading(false);
      setLoadingStatus(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoadingActivity(true);
    try {
      // Use the dedicated internal user activity endpoint
      const res = await axios.get(`${API}/internal/users/${userId}/activity`, {
        params: { limit: 100 },
        withCredentials: true
      });
      setActivityLogs(res.data.logs || []);
    } catch (error) {
      console.error("Failed to fetch activity logs:", error);
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/internal/users/${userId}/details`, editForm, { withCredentials: true });
      toast.success("User details updated successfully");
      setIsEditing(false);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (sendEmail = false) => {
    setResettingPassword(true);
    try {
      const res = await axios.post(`${API}/internal/users/${userId}/reset-password`, {
        send_email: sendEmail
      }, { withCredentials: true });
      setTempPassword(res.data.temp_password);
      setShowPasswordDialog(true);
      if (sendEmail) {
        toast.success("Password reset and sent via email");
      } else {
        toast.success("Password reset successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.subject || !emailForm.message) {
      toast.error("Please fill in subject and message");
      return;
    }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/internal/users/${userId}/send-email`, {
        subject: emailForm.subject,
        message: emailForm.message
      }, { withCredentials: true });
      toast.success(`Email sent to ${user.email}`);
      setShowEmailDialog(false);
      setEmailForm({ subject: "", message: "", template: "general" });
      setAttachments([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleTemplateChange = (templateId) => {
    const template = EMAIL_TEMPLATES.find(t => t.id === templateId);
    if (template) {
      setEmailForm({
        ...emailForm,
        template: templateId,
        subject: template.subject.replace("{name}", user?.name || ""),
        message: template.message.replace("{name}", user?.name || "")
      });
    }
  };

  const handleDelete = async () => {
    // Check super admin protection
    if (user?.role === 'super_admin' && superAdminCount <= 1) {
      toast.error("Cannot delete the last super admin");
      setShowDeleteDialog(false);
      return;
    }
    
    setDeleting(true);
    try {
      if (deleteType === 'permanent') {
        await axios.delete(`${API}/internal/users/${userId}/permanent`, { withCredentials: true });
        toast.success("User permanently deleted");
      } else {
        await axios.delete(`${API}/internal/users/${userId}`, { withCredentials: true });
        toast.success("User deactivated");
      }
      navigate('/backoffice/users');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRevokeAccess = async () => {
    // Check super admin protection
    if (user?.role === 'super_admin' && superAdminCount <= 1) {
      toast.error("Cannot revoke access for the last super admin");
      setShowRevokeDialog(false);
      return;
    }
    
    setRevoking(true);
    try {
      const res = await axios.post(`${API}/internal/users/${userId}/revoke-access`, {}, { withCredentials: true });
      toast.success(`Access revoked: ${res.data.sessions_terminated} sessions terminated, ${res.data.api_keys_revoked} API keys revoked`);
      setShowRevokeDialog(false);
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to revoke access");
    } finally {
      setRevoking(false);
    }
  };

  // Terminate all sessions for this internal user
  const handleTerminateAllSessions = async () => {
    if (!window.confirm(`This will log out ${user.name || user.email} from all devices. Continue?`)) return;
    
    setTerminatingSessions(true);
    try {
      const res = await axios.delete(`${API}/activity/admin/users/${userId}/sessions`, { withCredentials: true });
      toast.success(res.data.message || `Terminated ${res.data.terminated_count} session(s)`);
      fetchUserData(); // Refresh data to update session count
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to terminate sessions");
    } finally {
      setTerminatingSessions(false);
    }
  };

  const openBlockDialog = (action) => {
    setBlockAction(action);
    setBlockReason("");
    setShowBlockDialog(true);
  };

  const handleBlockUnblock = async () => {
    if (!blockReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    
    // Check super admin protection for blocking
    if (blockAction === "block" && user?.role === 'super_admin' && superAdminCount <= 1) {
      toast.error("Cannot block the last super admin");
      setShowBlockDialog(false);
      return;
    }
    
    setBlocking(true);
    try {
      await axios.put(`${API}/internal/users/${userId}/details`, {
        is_active: blockAction === "unblock",
        status_reason: blockReason,
        status_changed_at: new Date().toISOString(),
        status_changed_by: currentUser?.user_id
      }, { withCredentials: true });
      
      toast.success(blockAction === "block" ? "User blocked successfully" : "User unblocked successfully");
      setShowBlockDialog(false);
      setBlockReason("");
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user status");
    } finally {
      setBlocking(false);
    }
  };

  const handleRestoreAccess = async () => {
    try {
      await axios.post(`${API}/internal/users/${userId}/restore-access`, {}, { withCredentials: true });
      toast.success("Access restored");
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore access");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getRoleColor = (role) => {
    const colors = {
      super_admin: "bg-violet-600",
      admin: "bg-blue-600",
      support: "bg-green-600",
      finance: "bg-amber-600",
      marketing: "bg-pink-600",
      viewer: "bg-gray-600"
    };
    return colors[role] || "bg-gray-600";
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: isDark ? "bg-violet-500/20 text-violet-400" : "bg-violet-100 text-violet-700",
      admin: isDark ? "bg-blue-500/20 text-blue-400" : "bg-blue-100 text-blue-700",
      support: isDark ? "bg-green-500/20 text-green-400" : "bg-green-100 text-green-700",
      finance: isDark ? "bg-amber-500/20 text-amber-400" : "bg-amber-100 text-amber-700",
      marketing: isDark ? "bg-pink-500/20 text-pink-400" : "bg-pink-100 text-pink-700",
      viewer: isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-700"
    };
    return colors[role] || (isDark ? "bg-slate-500/20 text-slate-400" : "bg-gray-100 text-gray-700");
  };

  const isLastSuperAdmin = user?.role === 'super_admin' && superAdminCount <= 1;
  const canModifyUser = userId !== currentUser?.user_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>User not found</p>
        <Button onClick={() => navigate('/backoffice/users')} className="mt-4">Back to Users</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className={`sticky top-0 z-10 -mx-6 px-6 py-4 ${isDark ? 'bg-slate-900/95 border-slate-700' : 'bg-white/95 border-gray-200'} border-b backdrop-blur-sm`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/backoffice/users')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Users
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${getRoleColor(user.role)}`}>
                {user.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name}</h1>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{user.email}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Status badges */}
            <div className="flex items-center gap-2 mr-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role)}`}>
                {BUILT_IN_ROLES[user.role]?.name || user.role}
              </span>
              {user.is_active ? (
                <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Active
                </span>
              ) : (
                <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
                  <Ban className="w-3 h-3" /> Blocked
                </span>
              )}
              {user.access_revoked && (
                <span className="px-2 py-1 text-xs rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex items-center gap-1">
                  <ShieldOff className="w-3 h-3" /> Access Revoked
                </span>
              )}
            </div>
            <Button onClick={fetchUserData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Super Admin Warning */}
      {isLastSuperAdmin && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 ${isDark ? 'bg-amber-900/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>Last Super Admin</p>
            <p className={`text-sm ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
              This is the only super admin. Blocking, deleting, or revoking access is not allowed.
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - User Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* User Details Card */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>User Details</h2>
              {isEditing ? (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving}>
                    {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                    <Save className="w-4 h-4 mr-2" /> Save
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      placeholder="user@company.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Role</Label>
                    <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(BUILT_IN_ROLES).map(([key, role]) => (
                          <SelectItem key={key} value={key}>
                            <div className="flex items-center gap-2">
                              <Shield className="w-3 h-3" />
                              {role.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input
                      value={editForm.department}
                      onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
                      placeholder="e.g., Engineering, Sales"
                    />
                  </div>
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+44 xxx xxx xxxx"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <User className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Full Name</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.name || 'N/A'}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Mail className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email Address</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.email}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Role</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {BUILT_IN_ROLES[user.role]?.name || user.role}
                    </p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      {BUILT_IN_ROLES[user.role]?.description}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Department</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.department || 'Not set'}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Phone Number</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.phone || 'Not set'}</p>
                  </div>
                  <div className={`p-4 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className={`w-4 h-4 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Member Since</span>
                    </div>
                    <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatDate(user.created_at)}</p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Activity Log */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Activity Log
              </h2>
              <Button variant="outline" size="sm" onClick={fetchActivityLogs}>
                <RefreshCw className={`w-4 h-4 ${loadingActivity ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {loadingActivity ? (
              <div className="flex items-center justify-center h-40">
                <RefreshCw className="w-6 h-6 animate-spin text-violet-500" />
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="text-center py-12">
                <Activity className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No activity recorded yet</p>
                <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  Activity will appear here as the user performs actions
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {activityLogs.map((log, idx) => (
                  <div 
                    key={log.log_id || idx} 
                    className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}>
                          <Activity className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-violet-600'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {log.action?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </p>
                          {log.details && (
                            <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        {formatDate(log.created_at || log.timestamp)}
                      </span>
                    </div>
                    <div className={`flex gap-4 mt-3 text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      {log.ip_address && (
                        <span className="flex items-center gap-1">
                          <Globe className="w-3 h-3" /> {log.ip_address}
                        </span>
                      )}
                      {log.browser && (
                        <span className="flex items-center gap-1">
                          <Monitor className="w-3 h-3" /> {log.browser}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Right Column - Actions & Status */}
        <div className="space-y-6">
          {/* Session & Security Info */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Session & Security</h3>
            <div className="space-y-4">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Active Sessions</span>
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {userStatus?.active_sessions || 0}
                  </span>
                </div>
              </div>
              
              {/* Last Login Details */}
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Last Login</span>
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {formatDate(user.last_login || userStatus?.last_login_details?.time)}
                  </span>
                </div>
                {userStatus?.last_login_details && (
                  <div className={`space-y-1 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {userStatus.last_login_details.ip_address && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-3 h-3" />
                        <span>IP: {userStatus.last_login_details.ip_address}</span>
                      </div>
                    )}
                    {userStatus.last_login_details.location && (
                      <div className="flex items-center gap-2">
                        <Globe className="w-3 h-3" />
                        <span>{userStatus.last_login_details.location}</span>
                      </div>
                    )}
                    {userStatus.last_login_details.browser && (
                      <div className="flex items-center gap-2">
                        <Monitor className="w-3 h-3" />
                        <span>{userStatus.last_login_details.browser}</span>
                      </div>
                    )}
                  </div>
                )}
                {!userStatus?.last_login_details && !user.last_login && (
                  <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Never logged in</p>
                )}
              </div>

              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Email Verified</span>
                  <span className={user.email_verified ? 'text-green-500' : 'text-amber-500'}>
                    {user.email_verified ? <CheckCircle className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Two-Factor Auth</span>
                  <span className={user.two_factor_enabled ? 'text-green-500' : 'text-slate-400'}>
                    {user.two_factor_enabled ? <CheckCircle className="w-5 h-5" /> : <X className="w-5 h-5" />}
                  </span>
                </div>
              </div>
              
              {/* Recent Login History */}
              {userStatus?.recent_logins?.length > 0 && (
                <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                  <p className={`text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Recent Logins</p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {userStatus.recent_logins.slice(0, 5).map((login, idx) => (
                      <div key={idx} className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'} flex justify-between`}>
                        <span>{login.ip_address || 'Unknown IP'}</span>
                        <span>{formatDate(login.login_time)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status History */}
              {userStatus?.status_reason && (
                <div className={`p-3 rounded-lg border ${isDark ? 'bg-amber-900/20 border-amber-500/30' : 'bg-amber-50 border-amber-200'}`}>
                  <p className={`text-sm font-medium mb-1 ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                    {user.is_active ? 'Last Unblock Reason' : 'Block Reason'}
                  </p>
                  <p className={`text-xs ${isDark ? 'text-amber-400/80' : 'text-amber-600'}`}>
                    {userStatus.status_reason}
                  </p>
                </div>
              )}
              
              {/* Logout All Sessions Button */}
              {(userStatus?.active_sessions > 0) && (
                <Button 
                  variant="outline"
                  className={`w-full justify-center gap-2 mt-3 ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'}`}
                  onClick={handleTerminateAllSessions}
                  disabled={terminatingSessions}
                >
                  <UserX className="w-4 h-4" />
                  {terminatingSessions ? "Logging out..." : `Log Out All ${userStatus.active_sessions} Session(s)`}
                </Button>
              )}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className={`p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <h3 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h3>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2"
                onClick={() => setShowEmailDialog(true)}
              >
                <Send className="w-4 h-4" /> Send Custom Email
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2"
                onClick={() => handleResetPassword(false)}
                disabled={resettingPassword}
              >
                <Key className="w-4 h-4" /> Reset Password
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2"
                onClick={() => handleResetPassword(true)}
                disabled={resettingPassword}
              >
                <Mail className="w-4 h-4" /> Reset & Email Password
              </Button>

              {user.access_revoked ? (
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-green-600 hover:text-green-700"
                  onClick={handleRestoreAccess}
                  disabled={!canModifyUser}
                >
                  <CheckCircle className="w-4 h-4" /> Restore Access
                </Button>
              ) : (
                <Button 
                  variant="outline" 
                  className="w-full justify-start gap-2 text-amber-600 hover:text-amber-700"
                  onClick={() => setShowRevokeDialog(true)}
                  disabled={!canModifyUser || isLastSuperAdmin}
                >
                  <ShieldOff className="w-4 h-4" /> Revoke All Access
                </Button>
              )}

              <Button 
                variant="outline" 
                className={`w-full justify-start gap-2 ${user.is_active ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700'}`}
                onClick={() => openBlockDialog(user.is_active ? "block" : "unblock")}
                disabled={!canModifyUser || (user.is_active && isLastSuperAdmin)}
              >
                {user.is_active ? (
                  <><Ban className="w-4 h-4" /> Block User</>
                ) : (
                  <><CheckCircle className="w-4 h-4" /> Unblock User</>
                )}
              </Button>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card className={`p-6 border-red-500/30 ${isDark ? 'bg-red-900/10' : 'bg-red-50'}`}>
            <h3 className="font-semibold mb-4 text-red-600">Danger Zone</h3>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-900/20"
                onClick={() => { setDeleteType('soft'); setShowDeleteDialog(true); }}
                disabled={!canModifyUser || isLastSuperAdmin}
              >
                <UserX className="w-4 h-4" /> Deactivate User
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-2 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-900/20"
                onClick={() => { setDeleteType('permanent'); setShowDeleteDialog(true); }}
                disabled={!canModifyUser || isLastSuperAdmin}
              >
                <Trash2 className="w-4 h-4" /> Permanently Delete
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Temporary Password</DialogTitle>
            <DialogDescription>Share this password securely with the user</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className={`p-4 rounded-lg text-center font-mono text-lg ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>
              {tempPassword}
            </div>
            <p className={`text-sm mt-3 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
              The user must change this password on first login.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(tempPassword);
              toast.success("Password copied to clipboard");
            }}>
              <Copy className="w-4 h-4 mr-2" /> Copy Password
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-violet-500" />
              Send Email to {user.name}
            </DialogTitle>
            <DialogDescription>Compose a professional message to send to {user.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Email Template</Label>
              <Select value={emailForm.template} onValueChange={handleTemplateChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATES.map(template => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {template.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Email subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Your message..."
                rows={8}
                className="font-mono text-sm"
              />
            </div>
            <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Paperclip className="w-4 h-4" />
                <span className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Attachments (Coming Soon)</span>
              </div>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                File attachment support will be available in a future update.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEmailDialog(false);
              setEmailForm({ subject: "", message: "", template: "general" });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSendEmail} disabled={sendingEmail || !emailForm.subject || !emailForm.message}>
              {sendingEmail && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {deleteType === 'permanent' ? (
                <><Trash2 className="w-5 h-5 text-red-500" /> Permanently Delete User</>
              ) : (
                <><UserX className="w-5 h-5 text-amber-500" /> Deactivate User</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                {deleteType === 'permanent'
                  ? `Are you sure you want to permanently delete ${user.name}? This will remove ALL their data and cannot be undone.`
                  : `Are you sure you want to deactivate ${user.name}? They will not be able to log in but their data will be preserved.`
                }
              </p>
              {isLastSuperAdmin && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  This is the last super admin. Deletion is not allowed.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting || isLastSuperAdmin}
              className={deleteType === 'permanent' ? "bg-red-500 hover:bg-red-600" : "bg-amber-500 hover:bg-amber-600"}
            >
              {deleting && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {deleteType === 'permanent' ? 'Permanently Delete' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Access Dialog */}
      <AlertDialog open={showRevokeDialog} onOpenChange={setShowRevokeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldOff className="w-5 h-5" />
              Revoke All Access
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will immediately:</p>
              <ul className="list-disc ml-5 space-y-1 text-sm">
                <li>Terminate all active sessions</li>
                <li>Revoke all API keys</li>
                <li>Force the user to log in again</li>
              </ul>
              <p className="font-medium mt-2">User: {user.name} ({user.email})</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeAccess}
              disabled={revoking || isLastSuperAdmin}
              className="bg-red-500 hover:bg-red-600"
            >
              {revoking && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              Revoke Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Block/Unblock Dialog with Reason */}
      <AlertDialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {blockAction === "block" ? (
                <><Ban className="w-5 h-5 text-red-500" /> Block User</>
              ) : (
                <><CheckCircle className="w-5 h-5 text-green-500" /> Unblock User</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                {blockAction === "block" 
                  ? `Are you sure you want to block ${user?.name}? They will not be able to log in.`
                  : `Are you sure you want to unblock ${user?.name}? They will regain access to their account.`
                }
              </p>
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Reason <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder={blockAction === "block" 
                    ? "e.g., Violation of terms, security concern, account review..." 
                    : "e.g., Issue resolved, verification complete, request approved..."
                  }
                  rows={3}
                  className={isDark ? 'bg-slate-700 border-slate-600' : ''}
                />
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                  This will be recorded in the user&apos;s activity history.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setBlockReason(""); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBlockUnblock}
              disabled={blocking || !blockReason.trim()}
              className={blockAction === "block" ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}
            >
              {blocking && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
              {blockAction === "block" ? "Block User" : "Unblock User"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
